/**
 * Tajima DST 쓰기·읽기.
 *  - 512바이트 머리글: LA(이름) ST(레코드 수) CO(색 바꿈 수) +X -X +Y -Y(범위, 0.1mm) AX AY(끝 위치) MX MY PD, 0x1A, 공백 채움
 *  - 3바이트 레코드: 균형 3진법(±1,3,9,27,81) 비트로 dx·dy (한 레코드 최대 ±121 = 12.1mm), 바이트2 의 bit7 = 점프, 0xC3 = 색 바꿈, 끝 = 00 00 F3
 *  - DST 는 y 가 위로 증가한다 (계획은 아래로 증가 → 뒤집어 쓴다)
 *  - 트림: 알짜 이동 0 인 점프 3개 (+2,+2)(-4,-4)(+2,+2). 대부분의 타지마 호환기는 연속 점프 3개를 트림으로 본다.
 * 비트 배치와 트림 관례는 pyembroidery (MIT) 의 DstWriter 를 참고했다. NOTICE.md 참고.
 */
import { COLOR, END, JUMP, MAX_STEP, STITCH, type StitchPlan, TRIM } from "../engine/plan";
import { asciiLabel, ByteWriter } from "./bytes";

export const DST_HEADER = 512;

/** v ∈ [-121, 121] 를 균형 3진법 자릿수 [1,3,9,27,81] 로 */
export function ternary(v: number): number[] {
  if (!Number.isInteger(v) || v < -121 || v > 121) throw new RangeError(`DST 이동량 범위 초과: ${v}`);
  const d = [0, 0, 0, 0, 0];
  let x = v;
  for (let i = 0; i < 5; i++) {
    let r = ((x % 3) + 3) % 3;
    if (r === 2) r = -1;
    d[i] = r;
    x = (x - r) / 3;
  }
  return d;
}

/** dx, dy: DST 좌표(y 위로). kind: 0 바늘땀, 1 점프, 2 색 바꿈, 3 끝 */
export function encodeRecord(dx: number, dy: number, kind: 0 | 1 | 2 | 3): [number, number, number] {
  if (kind === 2) return [0, 0, 0xc3];
  if (kind === 3) return [0, 0, 0xf3];
  const x = ternary(dx),
    y = ternary(dy);
  let b0 = 0,
    b1 = 0,
    b2 = 0x03;
  // x
  if (x[0] === 1) b0 |= 1 << 0;
  if (x[0] === -1) b0 |= 1 << 1;
  if (x[2] === 1) b0 |= 1 << 2;
  if (x[2] === -1) b0 |= 1 << 3;
  if (x[1] === 1) b1 |= 1 << 0;
  if (x[1] === -1) b1 |= 1 << 1;
  if (x[3] === 1) b1 |= 1 << 2;
  if (x[3] === -1) b1 |= 1 << 3;
  if (x[4] === 1) b2 |= 1 << 2;
  if (x[4] === -1) b2 |= 1 << 3;
  // y
  if (y[0] === 1) b0 |= 1 << 7;
  if (y[0] === -1) b0 |= 1 << 6;
  if (y[2] === 1) b0 |= 1 << 5;
  if (y[2] === -1) b0 |= 1 << 4;
  if (y[1] === 1) b1 |= 1 << 7;
  if (y[1] === -1) b1 |= 1 << 6;
  if (y[3] === 1) b1 |= 1 << 5;
  if (y[3] === -1) b1 |= 1 << 4;
  if (y[4] === 1) b2 |= 1 << 5;
  if (y[4] === -1) b2 |= 1 << 4;
  if (kind === 1) b2 |= 1 << 7;
  return [b0, b1, b2];
}

export interface DstRecord {
  dx: number;
  dy: number;
  kind: 0 | 1 | 2 | 3;
}

export function decodeRecord(b0: number, b1: number, b2: number): DstRecord {
  if ((b2 & 0xf3) === 0xf3) return { dx: 0, dy: 0, kind: 3 };
  let x = 0,
    y = 0;
  if (b0 & 0x01) x += 1;
  if (b0 & 0x02) x -= 1;
  if (b0 & 0x04) x += 9;
  if (b0 & 0x08) x -= 9;
  if (b0 & 0x80) y += 1;
  if (b0 & 0x40) y -= 1;
  if (b0 & 0x20) y += 9;
  if (b0 & 0x10) y -= 9;
  if (b1 & 0x01) x += 3;
  if (b1 & 0x02) x -= 3;
  if (b1 & 0x04) x += 27;
  if (b1 & 0x08) x -= 27;
  if (b1 & 0x80) y += 3;
  if (b1 & 0x40) y -= 3;
  if (b1 & 0x20) y += 27;
  if (b1 & 0x10) y -= 27;
  if (b2 & 0x04) x += 81;
  if (b2 & 0x08) x -= 81;
  if (b2 & 0x20) y += 81;
  if (b2 & 0x10) y -= 81;
  if ((b2 & 0xc3) === 0xc3) return { dx: x, dy: y, kind: 2 };
  return { dx: x, dy: y, kind: b2 & 0x80 ? 1 : 0 };
}

const pad = (n: number, w: number) => String(n).padStart(w, " ");
const signed = (n: number, w: number) => (n >= 0 ? "+" : "-") + pad(Math.abs(n), w);

export function writeDst(plan: StitchPlan, name = "JASU"): Uint8Array {
  // 레코드 먼저
  const recs: [number, number, number][] = [];
  let px = 0,
    py = 0;
  let minX = 0,
    maxX = 0,
    minY = 0,
    maxY = 0;
  let colorChanges = 0;
  for (let i = 0; i < plan.cmds.length; i++) {
    const c = plan.cmds[i];
    const x = plan.xs[i],
      y = plan.ys[i];
    if (c === STITCH || c === JUMP) {
      let dx = x - px,
        dy = y - py;
      // 안전장치: 계획이 이미 나눠 두지만, 넘으면 여러 레코드로
      const steps = Math.max(1, Math.ceil(Math.max(Math.abs(dx), Math.abs(dy)) / MAX_STEP));
      let cx = px,
        cy = py;
      for (let s = 1; s <= steps; s++) {
        const nx = px + Math.round((dx * s) / steps),
          ny = py + Math.round((dy * s) / steps);
        recs.push(encodeRecord(nx - cx, -(ny - cy), c === JUMP ? 1 : 0));
        cx = nx;
        cy = ny;
      }
      px = x;
      py = y;
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    } else if (c === TRIM) {
      recs.push(encodeRecord(2, 2, 1), encodeRecord(-4, -4, 1), encodeRecord(2, 2, 1));
    } else if (c === COLOR) {
      recs.push(encodeRecord(0, 0, 2));
      colorChanges++;
    } else if (c === END) {
      break;
    }
  }
  const w = new ByteWriter(DST_HEADER + recs.length * 3 + 3);
  const label = asciiLabel(name, 16).padEnd(16, " ");
  w.ascii(`LA:${label}\r`);
  w.ascii(`ST:${pad(recs.length, 7)}\r`);
  w.ascii(`CO:${pad(colorChanges, 3)}\r`);
  // DST 는 y 위로: +Y = 위쪽 범위 = -minY(아래 방향 좌표)
  w.ascii(`+X:${pad(Math.max(0, maxX), 5)}\r`);
  w.ascii(`-X:${pad(Math.max(0, -minX), 5)}\r`);
  w.ascii(`+Y:${pad(Math.max(0, -minY), 5)}\r`);
  w.ascii(`-Y:${pad(Math.max(0, maxY), 5)}\r`);
  w.ascii(`AX:${signed(px, 5)}\r`);
  w.ascii(`AY:${signed(-py, 5)}\r`);
  w.ascii(`MX:${signed(0, 5)}\r`);
  w.ascii(`MY:${signed(0, 5)}\r`);
  w.ascii(`PD:******\r`);
  w.u8(0x1a);
  while (w.pos < DST_HEADER) w.u8(0x20);
  for (const r of recs) w.bytes(r);
  w.bytes([0x00, 0x00, 0xf3]);
  return w.result();
}

export interface DstParsed {
  header: Record<string, string>;
  /** 누적 좌표 (DST y 위로) 와 종류 */
  points: { x: number; y: number; kind: 0 | 1 | 2 | 3 }[];
  records: number;
}

export function readDst(bytes: Uint8Array): DstParsed {
  const head = new TextDecoder("latin1").decode(bytes.subarray(0, DST_HEADER));
  const header: Record<string, string> = {};
  for (const line of head.split("\r")) {
    const m = /^([A-Z+-]{2}):(.*)$/.exec(line.replace(/\x1a.*$/s, ""));
    if (m) header[m[1]] = m[2];
  }
  const points: DstParsed["points"] = [];
  let x = 0,
    y = 0,
    records = 0;
  for (let i = DST_HEADER; i + 2 < bytes.length; i += 3) {
    const r = decodeRecord(bytes[i], bytes[i + 1], bytes[i + 2]);
    records++;
    if (r.kind === 3) {
      points.push({ x, y, kind: 3 });
      break;
    }
    x += r.dx;
    y += r.dy;
    points.push({ x, y, kind: r.kind });
  }
  return { header, points, records };
}
